import { useEffect, useMemo, useState } from "react";
import { Loader2, Check, X, Pencil, Trash2, Users, Search, UserPlus, UserMinus } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/auth";
import { CURRICULA, DESIGNATIONS } from "@/pages/institution/InstitutionAuth";

const inputCls = "w-full rounded-xl border border-ink/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35";
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const STATUS_STYLE = {
  pending: "bg-iqamber/15 text-iqamber",
  approved: "bg-iqgreen/15 text-iqgreen",
  rejected: "bg-iqpink/15 text-iqpink",
};

const fmtErr = (d) => (typeof d === "string" ? d : "Action failed");

function EditModal({ inst, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: inst.name, curriculum: inst.curriculum || CURRICULA[0], address: inst.address || "",
    official_name: inst.officialName || "", designation: inst.designation || DESIGNATIONS[0], mobile: inst.mobile || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authFetch(`/api/admin/institutions/${inst.id}`, { method: "PUT", body: JSON.stringify(form) });
      const data = await res.json();
      if (!res.ok) throw new Error(fmtErr(data.detail));
      toast.success("Institution updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.message);
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={save} className="relative w-full max-w-lg bg-paper rounded-[1.8rem] shadow-2xl p-8 max-h-[88vh] overflow-y-auto space-y-3" data-testid="inst-edit-modal">
        <h3 className="font-display font-bold text-xl tracking-tight">Edit institution</h3>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Institution name" required className={inputCls} data-testid="inst-edit-name" />
        <select value={form.curriculum} onChange={(e) => setForm({ ...form, curriculum: e.target.value })} className={inputCls} data-testid="inst-edit-curriculum">
          {CURRICULA.map((c) => <option key={c}>{c}</option>)}
        </select>
        <textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" required rows={2} className={`${inputCls} resize-none`} data-testid="inst-edit-address" />
        <input value={form.official_name} onChange={(e) => setForm({ ...form, official_name: e.target.value })} placeholder="Name of the official" required className={inputCls} data-testid="inst-edit-official" />
        <select value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} className={inputCls} data-testid="inst-edit-designation">
          {DESIGNATIONS.map((d) => <option key={d}>{d}</option>)}
        </select>
        <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile number" required className={inputCls} data-testid="inst-edit-mobile" />
        <div className="flex gap-3 pt-2">
          <button type="submit" disabled={busy} data-testid="inst-edit-save" className="flex-1 flex items-center justify-center gap-2 bg-ink text-paper font-semibold py-3 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
          <button type="button" onClick={onClose} data-testid="inst-edit-cancel" className="px-6 rounded-full border border-ink/15 font-semibold text-sm hover:border-ink/40 transition-colors">Cancel</button>
        </div>
      </form>
    </div>
  );
}

function StudentsModal({ inst, onClose }) {
  const [students, setStudents] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => authFetch(`/api/admin/institutions/${inst.id}/students`).then((r) => r.json()).then((d) => setStudents(d.students || [])).catch(() => toast.error("Failed to load students"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const assign = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await authFetch(`/api/admin/institutions/${inst.id}/assign`, { method: "POST", body: JSON.stringify({ email }) });
      const data = await res.json();
      if (!res.ok) throw new Error(fmtErr(data.detail));
      toast.success(`${data.user.name} linked`);
      setEmail("");
      load();
    } catch (err) {
      toast.error(err.message);
    }
    setBusy(false);
  };

  const unassign = async (uid) => {
    const res = await authFetch(`/api/admin/institutions/${inst.id}/unassign`, { method: "POST", body: JSON.stringify({ user_id: uid }) });
    if (res.ok) { toast.success("Student unlinked"); load(); } else toast.error("Failed");
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-paper rounded-[1.8rem] shadow-2xl p-8 max-h-[88vh] overflow-y-auto" data-testid="inst-students-modal">
        <h3 className="font-display font-bold text-xl tracking-tight">{inst.name} — students</h3>
        <form onSubmit={assign} className="mt-5 flex gap-2.5">
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Assign student by email" required className={`${inputCls} flex-1`} data-testid="inst-assign-email-input" />
          <button type="submit" disabled={busy} data-testid="inst-assign-button" className="shrink-0 flex items-center gap-2 bg-ink text-paper font-semibold text-xs px-5 rounded-full hover:bg-iqblue disabled:opacity-60 transition-colors">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />} Assign
          </button>
        </form>
        <div className="mt-5 rounded-2xl bg-cream border border-ink/10 overflow-hidden">
          {!students ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-iqblue" /></div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-ink/45 uppercase tracking-wider">
                  <th className="px-5 py-3 font-semibold">Student</th>
                  <th className="px-3 py-3 font-semibold">Grade</th>
                  <th className="px-3 py-3 font-semibold">Reports</th>
                  <th className="px-3 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody>
                {students.map((s) => (
                  <tr key={s.id} className="border-t border-ink/5" data-testid={`inst-member-row-${s.id}`}>
                    <td className="px-5 py-3">
                      <div className="font-semibold text-ink/85">{s.name}</div>
                      <div className="text-ink/45">{s.email}</div>
                    </td>
                    <td className="px-3 py-3 text-ink/70">{s.grade || "—"}</td>
                    <td className="px-3 py-3 text-ink/70">{s.classicReports.length + s.discoveryReports.length}</td>
                    <td className="px-3 py-3 text-right">
                      <button onClick={() => unassign(s.id)} data-testid={`inst-unassign-${s.id}`} className="h-7 w-7 rounded-full border border-ink/15 inline-flex items-center justify-center text-ink/50 hover:border-iqpink hover:text-iqpink transition-colors" aria-label="Unlink student">
                        <UserMinus className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
                {students.length === 0 && <tr><td colSpan={4} className="px-5 py-8 text-center text-ink/40">No students linked yet.</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        <button onClick={onClose} data-testid="inst-students-close" className="mt-5 w-full border border-ink/15 font-semibold text-sm py-3 rounded-full hover:border-ink/40 transition-colors">Close</button>
      </div>
    </div>
  );
}

export default function InstitutionsTab() {
  const [institutions, setInstitutions] = useState(null);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [editInst, setEditInst] = useState(null);
  const [studentsInst, setStudentsInst] = useState(null);

  const load = () => authFetch("/api/admin/institutions").then((r) => r.json()).then((d) => setInstitutions(d.institutions || [])).catch(() => toast.error("Failed to load institutions"));
  useEffect(() => { load(); }, []);

  const act = async (iid, action) => {
    const res = await authFetch(`/api/admin/institutions/${iid}/${action}`, { method: "POST" });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(fmtErr(data.detail)); return; }
    toast.success(action === "approve" ? "Institution approved — join code emailed" : "Institution rejected");
    load();
  };

  const remove = async (inst) => {
    if (!window.confirm(`Delete ${inst.name}? Linked students will be unlinked (their accounts stay).`)) return;
    const res = await authFetch(`/api/admin/institutions/${inst.id}`, { method: "DELETE" });
    if (res.ok) { toast.success("Institution deleted"); load(); } else toast.error("Delete failed");
  };

  const list = useMemo(() => {
    let l = institutions || [];
    if (filter !== "all") l = l.filter((i) => i.status === filter);
    const q = query.trim().toLowerCase();
    if (q) l = l.filter((i) => i.name.toLowerCase().includes(q) || (i.email || "").toLowerCase().includes(q) || (i.officialName || "").toLowerCase().includes(q));
    return l;
  }, [institutions, filter, query]);

  if (!institutions) return <div className="py-16 flex justify-center"><Loader2 className="h-7 w-7 animate-spin text-iqblue" /></div>;

  const pendingCount = institutions.filter((i) => i.status === "pending").length;

  return (
    <div className="space-y-5" data-testid="tab-institutions">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-1.5" data-testid="inst-status-filters">
          {["all", "pending", "approved", "rejected"].map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`inst-filter-${f}`}
              className={`px-4 py-2 rounded-full text-xs font-bold capitalize transition-colors ${filter === f ? "bg-ink text-paper" : "bg-cream border border-ink/10 text-ink/60 hover:text-ink"}`}>
              {f}{f === "pending" && pendingCount > 0 ? ` (${pendingCount})` : ""}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs sm:ml-auto">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink/35" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search institutions" data-testid="inst-admin-search"
            className="w-full rounded-full border border-ink/15 bg-white pl-10 pr-4 py-2.5 text-xs outline-none focus:border-iqblue focus:ring-4 focus:ring-iqblue/15 transition-all placeholder:text-ink/35" />
        </div>
      </div>

      <div className="rounded-3xl bg-cream border border-ink/10 overflow-hidden" data-testid="institutions-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-ink/45 uppercase tracking-wider">
                <th className="px-6 py-3 font-semibold">Institution</th>
                <th className="px-3 py-3 font-semibold">Official</th>
                <th className="px-3 py-3 font-semibold">Contact</th>
                <th className="px-3 py-3 font-semibold">Code</th>
                <th className="px-3 py-3 font-semibold">Students</th>
                <th className="px-3 py-3 font-semibold">Status</th>
                <th className="px-3 py-3 font-semibold">Registered</th>
                <th className="px-3 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.id} className="border-t border-ink/5 hover:bg-white/60 transition-colors" data-testid={`inst-row-${i.id}`}>
                  <td className="px-6 py-3">
                    <div className="font-semibold text-ink/85 text-sm">{i.name}</div>
                    <div className="text-ink/45">{i.curriculum}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="font-semibold text-ink/80">{i.officialName}</div>
                    <div className="text-ink/45">{i.designation}</div>
                  </td>
                  <td className="px-3 py-3">
                    <div className="text-ink/70">{i.email}</div>
                    <div className="text-ink/45">{i.mobile}</div>
                  </td>
                  <td className="px-3 py-3 font-mono font-bold tracking-[0.15em] text-iqblue">{i.code}</td>
                  <td className="px-3 py-3 text-ink/70 font-semibold">{i.students}</td>
                  <td className="px-3 py-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide ${STATUS_STYLE[i.status] || STATUS_STYLE.pending}`} data-testid={`inst-status-${i.id}`}>{i.status}</span>
                  </td>
                  <td className="px-3 py-3 whitespace-nowrap text-ink/55">{fmtDate(i.createdAt)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-1.5">
                      {i.status === "pending" && (
                        <>
                          <button onClick={() => act(i.id, "approve")} data-testid={`inst-approve-${i.id}`} title="Approve" className="h-7 w-7 rounded-full bg-iqgreen/15 text-iqgreen inline-flex items-center justify-center hover:brightness-110 transition-all"><Check className="h-3.5 w-3.5" /></button>
                          <button onClick={() => act(i.id, "reject")} data-testid={`inst-reject-${i.id}`} title="Reject" className="h-7 w-7 rounded-full bg-iqpink/15 text-iqpink inline-flex items-center justify-center hover:brightness-110 transition-all"><X className="h-3.5 w-3.5" /></button>
                        </>
                      )}
                      <button onClick={() => setStudentsInst(i)} data-testid={`inst-students-${i.id}`} title="Students" className="h-7 w-7 rounded-full border border-ink/15 text-ink/55 inline-flex items-center justify-center hover:border-iqblue hover:text-iqblue transition-colors"><Users className="h-3.5 w-3.5" /></button>
                      <button onClick={() => setEditInst(i)} data-testid={`inst-edit-${i.id}`} title="Edit" className="h-7 w-7 rounded-full border border-ink/15 text-ink/55 inline-flex items-center justify-center hover:border-iqblue hover:text-iqblue transition-colors"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => remove(i)} data-testid={`inst-delete-${i.id}`} title="Delete" className="h-7 w-7 rounded-full border border-ink/15 text-ink/55 inline-flex items-center justify-center hover:border-iqpink hover:text-iqpink transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr><td colSpan={8} className="px-6 py-10 text-center text-ink/40" data-testid="institutions-empty">No institutions {filter !== "all" ? `with status "${filter}"` : "registered yet"}.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {editInst && <EditModal inst={editInst} onClose={() => setEditInst(null)} onSaved={load} />}
      {studentsInst && <StudentsModal inst={studentsInst} onClose={() => { setStudentsInst(null); load(); }} />}
    </div>
  );
}
